import type {ColumnSeries} from '@spaced-out/ui-design-system/lib/components/Charts';
import type {Drilldown} from '@spaced-out/ui-design-system/lib/types/charts';
import type {
  DataAuditWritebackBreakdownGraphsState,
  DataAuditWritebackGraphApi,
  DataAuditWritebackType,
} from 'src/components/data-audit/types';

import {DATA_AUDIT_WRITEBACK_BREAKDOWN_ORDER} from './constants';
import type {LabelTranslationFn} from './label-types';
import {formatDataAuditSourceLabel} from './writeback-labels';
import {getDataAuditWritebackBreakdownCategoryLabel} from './writeback-type';


export function isDataAuditWritebackBreakdownGraphEmpty(
  graphs: DataAuditWritebackBreakdownGraphsState,
): boolean {
  return DATA_AUDIT_WRITEBACK_BREAKDOWN_ORDER.every((writebackType) => {
    const graph = graphs[writebackType];

    if (!graph) {
      return true;
    }

    return graph.successCount === 0 && graph.failureCount === 0;
  });
}

function getDataAuditWritebackBreakdownDrilldownLabel(
  writebackType: DataAuditWritebackType,
  t: LabelTranslationFn,
): string {
  const labelMap: Record<DataAuditWritebackType, string> = {
    nwb: t('Note_Writebacks', 'Note Writebacks'),
    fwb: t('Field_Update_Writebacks', 'Field Update Writebacks'),
    owb: t('Object_Create_Writebacks', 'Object Create Writebacks'),
  };

  return labelMap[writebackType];
}

function buildDataAuditWritebackSourceDrilldownData(
  graph: DataAuditWritebackGraphApi | null,
  writebackType: DataAuditWritebackType,
  drilldownLabel: string,
  t: LabelTranslationFn,
) {
  const sources = [...(graph?.sourceWiseCount ?? [])].sort(
    (left, right) =>
      right.successCount +
      right.failureCount -
      (left.successCount + left.failureCount),
  );

  if (sources.length === 0) {
    return [
      {
        name: drilldownLabel,
        y: 0,
      },
    ];
  }

  return sources.map((sourceEntry) => ({
    name: formatDataAuditSourceLabel(sourceEntry.source, writebackType, t),
    y: sourceEntry.successCount + sourceEntry.failureCount,
  }));
}

export function buildDataAuditWritebackBreakdownChartData(
  graphs: DataAuditWritebackBreakdownGraphsState,
  t: LabelTranslationFn,
): {
  series: Array<ColumnSeries>;
  drilldown: Drilldown;
} {
  const successfulLabel = t('Successful', 'Successful');
  const failedLabel = t('Failed', 'Failed');
  const sourceProductLabel = t('Source_Product', 'Source Product');

  const successfulData = DATA_AUDIT_WRITEBACK_BREAKDOWN_ORDER.map(
    (writebackType) => {
      const graph = graphs[writebackType];
      const categoryLabel = getDataAuditWritebackBreakdownCategoryLabel(
        writebackType,
        t,
      );

      return {
        name: categoryLabel,
        y: graph?.successCount ?? 0,
        drilldown: writebackType,
      };
    },
  );

  const failedData = DATA_AUDIT_WRITEBACK_BREAKDOWN_ORDER.map(
    (writebackType) => {
      const graph = graphs[writebackType];
      const categoryLabel = getDataAuditWritebackBreakdownCategoryLabel(
        writebackType,
        t,
      );

      return {
        name: categoryLabel,
        y: graph?.failureCount ?? 0,
        drilldown: writebackType,
      };
    },
  );

  const drilldownSeries = DATA_AUDIT_WRITEBACK_BREAKDOWN_ORDER.map(
    (writebackType) => {
      const graph = graphs[writebackType];
      const drilldownLabel = getDataAuditWritebackBreakdownDrilldownLabel(
        writebackType,
        t,
      );

      return {
        type: 'column' as const,
        id: writebackType,
        name: sourceProductLabel,
        data: buildDataAuditWritebackSourceDrilldownData(
          graph,
          writebackType,
          drilldownLabel,
          t,
        ),
      };
    },
  );

  return {
    series: [
      {
        name: successfulLabel,
        data: successfulData,
      },
      {
        name: failedLabel,
        data: failedData,
      },
    ],
    drilldown: {
      series: drilldownSeries,
    },
  };
}
